/**
 * Route-level code splitting. Each screen module is its own chunk, fetched the
 * first time its route renders, so the initial bundle carries only the shell
 * (sign-in, layout, router). Budgets: scripts/check-bundle-size.mjs.
 *
 * Once a chunk has loaded, the screen renders synchronously (no Suspense
 * fallback flash on later visits). `preloadRoutes()` loads every chunk; the
 * tests call it before rendering so they see screens immediately.
 */
import { track } from './quiet';
import { Component, lazy, Suspense, useState, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState, Loading } from './components/states';

const preloaders: Array<() => Promise<unknown>> = [];

/** A route screen (or a part of one, with props) whose module loads on first render. `load` resolves to the component. */
export function lazyRoute<P extends object = object>(load: () => Promise<ComponentType<P>>): ComponentType<P> {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;
  const fetchModule = (): Promise<ComponentType<P>> => {
    pending ??= track(load()).then(
      (component) => {
        loaded = component;
        return component;
      },
      (error: unknown) => {
        // Let a later render (the error screen's "Try again") fetch again
        pending = null;
        throw new Error('This screen could not be loaded', { cause: error });
      }
    );
    return pending;
  };
  const make = () => lazy(async () => ({ default: await fetchModule() }));
  let Lazy = make();
  preloaders.push(fetchModule);

  function LazyRoute(props: P) {
    // React.lazy's props type wraps P in ref handling; the screen takes exactly P
    const LazyScreen = Lazy as unknown as ComponentType<P>;
    // Bumped by "Try again": a new lazy() (React caches the failed one) and a fresh boundary
    const [attempt, setAttempt] = useState(0);
    // Decided once per mount: a screen first rendered before its module arrived keeps rendering
    // through the same boundary. Switching to the loaded component on a later render would be a
    // different element tree, so React would remount it and drop its state (a half-filled form).
    const [direct] = useState(() => loaded !== null);
    const Loaded = loaded;
    if (direct && Loaded) return <Loaded {...props} />;
    return (
      <RouteErrorBoundary
        key={attempt}
        onRetry={() => {
          Lazy = make();
          setAttempt((n) => n + 1);
        }}
      >
        <Suspense fallback={<Loading label="Loading screen" />}>
          <LazyScreen {...props} />
        </Suspense>
      </RouteErrorBoundary>
    );
  }
  return LazyRoute;
}

/** Load every lazy route's module (tests, or warming the cache). */
export async function preloadRoutes(): Promise<void> {
  // A loaded module can declare more screens (an area's own route table), so repeat until none are new
  for (let done = 0; done < preloaders.length; ) {
    const batch = preloaders.slice(done);
    done = preloaders.length;
    await Promise.all(batch.map((preload) => preload()));
  }
}

interface BoundaryProps {
  children: ReactNode;
  onRetry: () => void;
}

/** A chunk that fails to load (offline, or a deploy removed it) shows an error with a retry, not a blank page. */
class RouteErrorBoundary extends Component<BoundaryProps, { error: unknown }> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[web] a screen failed to load', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorState
          error={this.state.error}
          title="This screen could not be loaded"
          onRetry={this.props.onRetry}
        />
      );
    }
    return this.props.children;
  }
}
